"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SumoServer = void 0;
const location_1 = require("@/app/shared/constants/scheduler/location");
const helpers_1 = require("@/app/shared/helpers");
const utils_1 = require("@/app/shared/helpers/utils");
const constants_1 = require("@/app/shared/pages/redirect/constants");
const aws_amplify_1 = require("aws-amplify");
const scheduler_1 = require("../store/scheduler");
const constants_2 = require("../store/user/constants");
const CognitoService_1 = require("./CognitoService");
aws_amplify_1.Amplify.configure(SUMO1_AMPLIFY_CONFIG);
class SumoServerInstance {
    constructor() {
        Object.defineProperty(this, "isAuthorized", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                try {
                    await aws_amplify_1.Auth.currentAuthenticatedUser();
                    // Amplify auth throws an error if user is not authorized..
                    return true;
                }
                catch (error) {
                    console.warn(error);
                    return false;
                }
            }
        });
        Object.defineProperty(this, "logout", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                const isShowGuide = helpers_1.Storage.Local.getBoolean(helpers_1.Storage.Key.SETTINGS_SHOW_GUIDE);
                try {
                    await aws_amplify_1.Auth.signOut();
                }
                catch (error) {
                    console.error('error signing out: ', error);
                }
                helpers_1.Storage.Local.clearExcept(helpers_1.COGNITO_DEVICE_KEYS);
                if (isShowGuide !== undefined) {
                    helpers_1.Storage.Local.setBoolean(helpers_1.Storage.Key.SETTINGS_SHOW_GUIDE, isShowGuide);
                }
                return true;
            }
        });
        Object.defineProperty(this, "getUser", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                const currentUser = await aws_amplify_1.Auth.currentUserInfo();
                if (currentUser) {
                    return {
                        id: currentUser.attributes.sub,
                        name: currentUser.username,
                        email: currentUser.attributes.email,
                    };
                }
                throw new Error('User is not authorized!');
            }
        });
        Object.defineProperty(this, "getAuthenticationResponse", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                try {
                    const profile = await this.getUserProfile();
                    if (!profile)
                        throw new Error('Profile not found!');
                    const [roleResponse, licensesResponse, tenantResponse, workspacesResponse] = await Promise.all([
                        this.getCurrentRole(),
                        this.getLicenses({ tenantId: profile.tenantId }),
                        this.getTenantData({ tenantId: profile.tenantId }),
                        this.getWorkspaces(),
                    ]);
                    const role = roleResponse.role;
                    const license = licensesResponse.licenses[0];
                    const tenant = tenantResponse.tenant;
                    const workspaces = workspacesResponse.workspaces;
                    return {
                        userData: profile,
                        role,
                        license,
                        tenant,
                        workspaces,
                    };
                }
                catch (error) {
                    throw new Error(String(error));
                }
            }
        });
        Object.defineProperty(this, "getGoogleAccessToken", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (payload) => {
                return new Promise(async (resolve, reject) => {
                    try {
                        const defaultScope = ['openid', 'profile', 'email'].join(' ');
                        const scope = [
                            defaultScope,
                            'https://www.googleapis.com/auth/calendar.events',
                            'https://www.googleapis.com/auth/contacts.readonly',
                        ].join(' ');
                        const state = Date.now();
                        const url = helpers_1.query.set(`https://accounts.google.com/o/oauth2/v2/auth`, {
                            client_id: REACT_APP_GOOGLE_CLIENT_ID,
                            response_type: 'token',
                            redirect_uri: (0, utils_1.getRedirectUrl)(location_1.IntegrationType.GOOGLE_CALENDAR),
                            state,
                            flowName: payload.login ? 'GeneralOAuthFlow' : '',
                            scope: payload.login ? defaultScope : scope,
                            include_granted_scopes: true,
                            enable_granular_consent: true,
                            service: 'lso',
                            o2v: 2,
                            ddm: 0,
                            prompt: payload.login ? 'select_account' : 'consent',
                            ...(payload.loginHint ? { login_hint: payload.loginHint } : {}),
                        });
                        console.log(`🚀 ~ SumoServerInstance ~ url:`, url);
                        const Popup = payload.isOutlookAddon ? helpers_1.OutlookPopup : helpers_1.WindowPopup;
                        const popupResponse = await Popup.open(url, { width: 640, height: 520 });
                        if (popupResponse instanceof helpers_1.PromiseTimeoutError || popupResponse instanceof Error) {
                            throw popupResponse;
                        }
                        Popup.onMessage((data) => {
                            if (data.type === constants_1.AuthRedirectEvent.Google) {
                                if (data.access_token && data.state === state) {
                                    resolve(data);
                                }
                                Popup.close();
                            }
                        });
                    }
                    catch (error) {
                        reject(error);
                    }
                });
            }
        });
        Object.defineProperty(this, "getGoogleAuthorizationCode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (payload) => {
                return new Promise(async (resolve, reject) => {
                    try {
                        const scope = [
                            'openid',
                            'profile',
                            'email',
                            'https://www.googleapis.com/auth/calendar.events',
                            'https://www.googleapis.com/auth/contacts.readonly',
                        ].join(' ');
                        const state = Date.now();
                        const url = helpers_1.query.set(`https://accounts.google.com/o/oauth2/v2/auth`, {
                            client_id: REACT_APP_GOOGLE_CLIENT_ID,
                            response_type: 'code',
                            response_mode: 'query',
                            access_type: 'offline',
                            redirect_uri: (0, utils_1.getRedirectUrl)(location_1.IntegrationType.GOOGLE_CALENDAR),
                            state,
                            scope,
                            include_granted_scopes: true,
                            enable_granular_consent: true,
                            prompt: 'consent',
                            ...(payload.loginHint ? { login_hint: payload.loginHint } : {}),
                        });
                        const Popup = payload.isOutlookAddon ? helpers_1.OutlookPopup : helpers_1.WindowPopup;
                        const popupResponse = await Popup.open(url, { width: 640, height: 520 });
                        if (popupResponse instanceof helpers_1.PromiseTimeoutError || popupResponse instanceof Error) {
                            throw popupResponse;
                        }
                        Popup.onMessage((data) => {
                            if (data.type !== constants_1.AuthRedirectEvent.Google || data.state !== state)
                                return;
                            if (data.code) {
                                resolve(data);
                            }
                            else {
                                reject(new Error('Google authorization code missing'));
                            }
                            Popup.close();
                        });
                    }
                    catch (error) {
                        reject(error);
                    }
                });
            }
        });
        Object.defineProperty(this, "getMicrosoftAccessToken", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (payload) => {
                return new Promise(async (resolve, reject) => {
                    try {
                        const state = Date.now();
                        const url = helpers_1.query.set('https://login.microsoftonline.com/common/oauth2/v2.0/authorize', {
                            client_id: REACT_APP_MICROSOFT_CLIENT_ID,
                            response_type: 'code',
                            response_mode: 'query',
                            prompt: 'select_account',
                            redirect_uri: (0, utils_1.getRedirectUrl)(location_1.IntegrationType.MICROSOFT_CALENDAR),
                            scope: ['Calendars.ReadWrite', 'User.Read', 'Contacts.Read', 'offline_access'].join(' '),
                            state,
                        });
                        const Popup = payload.isOutlookAddon ? helpers_1.OutlookPopup : helpers_1.WindowPopup;
                        const popupResponse = await Popup.open(url, { width: 640, height: 520 });
                        if (popupResponse instanceof helpers_1.PromiseTimeoutError || popupResponse instanceof Error) {
                            throw popupResponse;
                        }
                        Popup.onMessage((data) => {
                            if (data.type === constants_1.AuthRedirectEvent.Microsoft) {
                                if (data.code && data.state === state) {
                                    resolve(data);
                                }
                                Popup.close();
                            }
                        });
                    }
                    catch (error) {
                        reject(error);
                    }
                });
            }
        });
        Object.defineProperty(this, "updateSalesforceTokens", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (access_token, refresh_token) => {
                helpers_1.Storage.Local.set(helpers_1.Storage.Key.SERVER_SALESFORCE_ACCESS_TOKEN, access_token);
                if (refresh_token) {
                    helpers_1.Storage.Local.set(helpers_1.Storage.Key.SERVER_SALESFORCE_REFRESH_TOKEN, refresh_token);
                }
            }
        });
        Object.defineProperty(this, "loginWithMicrosoftAuthorizationCode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (payload) => {
                return await this.login({ ...payload, integrationType: location_1.IntegrationType.MICROSOFT_CALENDAR });
            }
        });
        Object.defineProperty(this, "loginWithGoogleAuthorizationCode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (payload) => {
                return await this.login({ ...payload, integrationType: location_1.IntegrationType.GOOGLE_CALENDAR });
            }
        });
        Object.defineProperty(this, "login", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async ({ code, integrationType, isAuthorized }) => {
                const response = await (0, helpers_1.handleAPIRequest)(`${scheduler_1.API_PUBLIC}/auth`, {
                    code,
                    login: true,
                    redirectUrl: (0, utils_1.getRedirectUrl)(integrationType),
                    switchAccount: false,
                    userTimeZone: 'Europe/Warsaw',
                    updateIntegration: false,
                    integrationType,
                });
                if (response && !isAuthorized) {
                    await (0, helpers_1.withCognitoRetry)(async () => {
                        const cognitoUser = await aws_amplify_1.Auth.signIn(response.email);
                        await aws_amplify_1.Auth.sendCustomChallengeAnswer(cognitoUser, response.secret);
                    });
                }
                helpers_1.Storage.Local.set(helpers_1.Storage.Key.SERVER_TYPE, "SUMO" /* ServerType.SUMO */);
                return await this.getUser();
            }
        });
        Object.defineProperty(this, "connectGoogleContacts", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async ({ code }) => {
                await (0, helpers_1.handleAPIRequest)(`${scheduler_1.API_PRIVATE}/auth`, {
                    code,
                    login: false,
                    redirectUrl: (0, utils_1.getRedirectUrl)(location_1.IntegrationType.GOOGLE_CALENDAR),
                    switchAccount: false,
                    userTimeZone: 'Europe/Warsaw',
                    updateIntegration: true,
                    integrationType: location_1.IntegrationType.GOOGLE_CALENDAR,
                });
            }
        });
        Object.defineProperty(this, "getUserProfile", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                const { userData } = await this.getUserData();
                return userData?.[0];
            }
        });
        Object.defineProperty(this, "signInWithPassword", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (email, password, userTimeZone) => {
                const result = await (0, helpers_1.withCognitoRetry)(() => aws_amplify_1.Auth.signIn(email, password));
                if (result.challengeName === constants_2.SOFTWARE_TOKEN_MFA) {
                    CognitoService_1.CognitoService.setPendingMfaUser(result);
                    return { mfaRequired: true };
                }
                const authResponse = await this.completePasswordAuth(email, userTimeZone);
                return { mfaRequired: false, authResponse };
            }
        });
        Object.defineProperty(this, "confirmMfaCode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (code) => {
                const user = CognitoService_1.CognitoService.getPendingMfaUser();
                await aws_amplify_1.Auth.confirmSignIn(user, code, constants_2.SOFTWARE_TOKEN_MFA);
            }
        });
        Object.defineProperty(this, "passwordAuthComplete", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (request) => {
                return await (0, helpers_1.handleAPIRequest)(`${scheduler_1.API_PUBLIC}/auth/passwordComplete`, request);
            }
        });
        Object.defineProperty(this, "completeCustomChallenge", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (email, secret) => {
                await (0, helpers_1.withCognitoRetry)(async () => {
                    const cognitoUser = await aws_amplify_1.Auth.signIn(email);
                    await aws_amplify_1.Auth.sendCustomChallengeAnswer(cognitoUser, secret);
                });
            }
        });
        Object.defineProperty(this, "completePasswordAuth", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (email, userTimeZone) => {
                const response = await this.passwordAuthComplete({ email, userTimeZone });
                if (!response.secret) {
                    throw new Error('Password auth failed: no secret returned');
                }
                await this.completeCustomChallenge(email, response.secret);
                return response;
            }
        });
        Object.defineProperty(this, "checkEmail", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (email) => {
                return await (0, helpers_1.handleAPIRequest)(`${scheduler_1.API_PUBLIC}/auth/checkEmail`, { email });
            }
        });
        Object.defineProperty(this, "forgotPassword", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (payload) => {
                await aws_amplify_1.Auth.forgotPassword(payload.email);
            }
        });
        Object.defineProperty(this, "checkSSOAuth", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (payload) => {
                return await (0, helpers_1.handleAPIRequest)(`${scheduler_1.API_PUBLIC}/auth/sso/initiate`, payload);
            }
        });
    }
    async schedulerUpsertInboxBookingPage(parameters) {
        return await (0, helpers_1.handleAPIRequest)(`${scheduler_1.API_PRIVATE}/upsertInboxMeetingType`, parameters);
    }
    async getAdminDataByTenant(data) {
        return await (0, helpers_1.handleAPIRequest)(`${scheduler_1.API_PRIVATE}/getAdminDataByTenant`, data);
    }
    async getUserDataByTenant(data) {
        return await (0, helpers_1.handleAPIRequest)(`${scheduler_1.API_PRIVATE}/getUserDataByTenant`, data);
    }
    async getSchedulerMembers(data) {
        return await (0, helpers_1.handleAPIRequest)(`${scheduler_1.API_PRIVATE}/getMembers`, data);
    }
    async getSchedulerTeams(data) {
        return await (0, helpers_1.handleAPIRequest)(`${scheduler_1.API_PRIVATE}/getTeamsByWorkspaceId`, data);
    }
    async getLocations(data) {
        return await (0, helpers_1.handleAPIRequest)(`${scheduler_1.API_PRIVATE}/getLocations`, data);
    }
    async getHostSchedule(data) {
        return await (0, helpers_1.handleAPIRequest)(`${scheduler_1.API_PRIVATE}/getHostSchedule`, data);
    }
    async getUserData() {
        return await (0, helpers_1.handleAPIRequest)(`${scheduler_1.API_PRIVATE}/getListUserDataByUserId`, {});
    }
    async getCurrentRole() {
        return await (0, helpers_1.handleAPIRequest)(`${scheduler_1.API_PRIVATE}/getCurrentRole`, {});
    }
    async getLicenses(data) {
        return await (0, helpers_1.handleAPIRequest)(`${scheduler_1.API_PRIVATE}/getLicenses`, data);
    }
    async getTenantData(data) {
        return await (0, helpers_1.handleAPIRequest)(`${scheduler_1.API_PRIVATE}/getTenantByTenantId`, data);
    }
    async getWorkspaces() {
        return await (0, helpers_1.handleAPIRequest)(`${scheduler_1.API_PRIVATE}/getWorkspacesByTenantId`, {});
    }
    async getMeetingTypes(data) {
        return await (0, helpers_1.handleAPIRequest)(`${scheduler_1.API_PRIVATE}/getMeetingType`, data);
    }
    async createInstantEvent(data) {
        return await (0, helpers_1.handleAPIRequest)(`${scheduler_1.API_PRIVATE}/createInstantEvent`, data);
    }
}
exports.default = SumoServerInstance;
exports.SumoServer = new SumoServerInstance();
